/*
-------------------------------------------------------------------------------------
Authors         : Alinsonorin, John Myl B., Awi, Joseph Kahl L., Gozon, Daniel Allan
Date Created    : Jul 27, 2025
Folder          : src/components/button
File            : ButtonCompDark.tsx
Description     : 
    This reusable component is a dark button.
Copyright © 2025. All rights reserved.
-------------------------------------------------------------------------------------
*/


// Import React Systems
import {   useState, useEffect } from 'react';

// Imports MUI Components
import { TextField, Typography, Box, Stack, IconButton, InputAdornment } from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';

// Imports Functions
import { textfieldErrors } from "../../../functions/textfieldErrors";
import { fontFamily } from '@mui/system';
import { red } from '@mui/material/colors';

const TextFieldComp = ({ 
  id,
  label,
  type,
  value,
  color,
  onChange,
  state,
  special,
  search,
  requires,
  maxLength,
  readOnly,
  check,
  showPasswordToggle,
  showPassword,
  onTogglePassword,
  onBlur,
  placeholder
}) => {
 
  // Initialization
  const [input, setInput] = useState(value);
  const [inputError, setInputError] = useState(false);
  const [errorText, setErrorText] = useState("")
  const [confirmPassword, setConfirmPassword] = useState("")

  // Handle external validation trigger (check prop)
  // check can be a boolean or a string error message
  useEffect(() => {
    if (typeof check === 'string' && check.trim() !== "") {
      // If check is a string error message, display it
      setInputError(true);
      setErrorText(check);
    } else if (check && requires && (!value || value.trim() === "")) {
      // If check is boolean/truthy and field is required and empty
      setInputError(true);
      setErrorText(`${label || "This field"} is required`);
    } else if (check && value && value.trim() !== "" && typeof check !== 'string') {
      // Clear error if field has value when check is triggered (but not if check is a string)
      setInputError(false);
      setErrorText("");
    } else if (!check || (typeof check === 'string' && check.trim() === "")) {
      // Clear error if check is falsy or empty string
      setInputError(false);
      setErrorText("");
    }
  }, [check, requires, value, label]);

  // Handle change event for the text field
  const handleChange = (event) => {
    const newValue = event.target.value;
    setInput(newValue); // Update local state
    onChange(newValue); // Call the parent onChange handler
    
    // Clear external validation error when user starts typing
    if (check && newValue.trim() !== "") {
      setInputError(false);
      setErrorText("");
    }
    
    if (id === 'password' || id === 'confirmPassword') {
      setConfirmPassword(newValue);
      textfieldErrors(newValue, setInputError, setErrorText, id, confirmPassword);
    } else {
      textfieldErrors(newValue, setInputError, setErrorText, id);
    }
  };

  // Styling for the text field label
  const styleText = {
    color: '#2C251E',
    fontWeight: {
      xs : '600',
      sm : '600',
      md : '100',
      lg : '100',
      xl : '100',
    },
    fontSize: '14px',
    padding: '5px 0',
  };

  return (
    <div>
      <Box textAlign="start">
        {/* TextField Container */}
        <Stack direction="column">
            {/* Text */}
            <Typography variant="h3" sx={styleText}>
              <Stack direction="row" spacing={2}>
                {label}
                {requires && (id === "signin-email" || id === "signin-password") ? (
                  null
                ) : !requires ? (
                  null
                ) : (
                  <Typography
                    sx={{
                      color: "#9F4223",
                      padding: "0 3px"
                    }}
                  >
                    *
                  </Typography>
                )} 
              </Stack>
            </Typography>
      
            {/* TextField */}
            <TextField
                placeholder={placeholder}
                required={requires}
                error={Boolean(inputError)}
                id={id}
                type={type}
                value={value}
                variant="outlined"
                helperText={errorText}
                color={color}
                FormHelperTextProps={{
                  sx: { color: 'error.main' }
                }}
                InputProps={{
                  readOnly: readOnly || state,
                  endAdornment: showPasswordToggle && (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={onTogglePassword}
                        edge="end"
                        sx={{
                          margin: "0 0 0 0",
                          padding: 0,
                          color: '#2C251E',
                          '&:hover': {
                            backgroundColor: 'rgba(197, 165, 114, 0.2)',
                          },
                        }}
                      >
                        {value ? (!showPassword ? <VisibilityOff /> : <Visibility />) : null}
                      </IconButton>
                    </InputAdornment>
                  ),
                  sx: {
                    borderRadius: '10px',
                    fontSize: '14px',
                    color: 
                      special ? "rgba(44, 37, 30, .7)" : 
                      null,
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 
                        special ? "rgba(44, 37, 30, .4)" : 
                        'rgba(197, 165, 114, 0.4)',
                      borderWidth: '2px',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: 
                        special ? "rgba(44, 37, 30, .4)" :
                        inputError ? 'error.main' :
                        'rgba(197, 165, 114, 0.4)',
                    },
                    '&:hover': {
                      bgcolor: 
                        special ? "none" :
                        '#f0f0f0ff',
                    },
                    '& input::placeholder': {
                      color: "#2C251E",
                      opacity: .6,
                      fontSize: "12px"
                    },
                  },
                }}
                onChange={
                  id === "change-password" ? onChange:
                  handleChange
                }
                onBlur={onBlur}
                sx={{
                    width: '100%',
                    borderRadius: '10px',
                }}
                inputProps={{
                  style: {
                    // Style the placeholder: black for text, red for *
                    color: '#2C251E',
                  },
                }}
            />
      
            </Stack>
        </Box>
    </div>
  )
}

export default TextFieldComp
